import { useState, useEffect, useRef, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Swords, Play, Pause, Trophy, Users, Clock, Plus, Loader2, Volume2, VolumeX, Timer, Zap } from "lucide-react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import SEO from "../components/SEO";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;
const WS_URL = API.replace("https://", "wss://").replace("http://", "ws://");

export default function BattlesPage() {
  const [battles, setBattles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [creating, setCreating] = useState(false);
  const [selectedBattle, setSelectedBattle] = useState(null);
  const token = localStorage.getItem("token");
  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  const fetchBattles = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/battles/?status=${filter}`);
      setBattles(res.data);
    } catch {} finally { setLoading(false); }
  }, [filter]);

  useEffect(() => { fetchBattles(); }, [fetchBattles]);

  if (selectedBattle) {
    return <BattleArena battle={selectedBattle} onBack={() => { setSelectedBattle(null); fetchBattles(); }} token={token} headers={headers} />;
  }

  return (
    <div className="min-h-screen bg-dark pt-24 pb-16 px-4" data-testid="battles-page">
      <SEO title="Beat Battles" description="Real-time 1v1 beat competitions. Vote for your favorite producer!" />
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="text-center mb-10">
          <Badge className="bg-red-500/20 text-red-400 border-0 mb-4">Live</Badge>
          <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl uppercase tracking-tight mb-3">
            Beat <span className="text-neon-cyan">Battles</span>
          </h1>
          <p className="text-zinc-400 max-w-lg mx-auto text-sm sm:text-base">
            Real-time 1v1 beat competitions. Drop your beat, challenge a producer, let the crowd decide.
          </p>
        </div>

        {/* Controls */}
        <div className="flex flex-wrap gap-3 justify-between items-center mb-8">
          <div className="flex gap-2">
            {["all", "waiting", "live", "ended"].map(f => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={`px-4 py-1.5 rounded-full text-xs uppercase tracking-wider transition ${
                  filter === f ? "bg-neon-cyan text-black font-bold" : "bg-white/10 text-zinc-400 hover:bg-white/20"
                }`}
                data-testid={`battle-filter-${f}`}
              >
                {f}
              </button>
            ))}
          </div>
          <Button onClick={() => setCreating(!creating)} className="bg-neon-cyan text-black font-bold" data-testid="create-battle-btn">
            <Plus className="w-4 h-4 mr-2" /> Start Battle
          </Button>
        </div>

        {/* Create Form */}
        <AnimatePresence>
          {creating && <CreateBattleForm token={token} headers={headers} onCreated={(b) => { setBattles(prev => [b, ...prev]); setCreating(false); }} />}
        </AnimatePresence>

        {/* Battle List */}
        {loading ? (
          <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-neon-cyan" /></div>
        ) : battles.length === 0 ? (
          <div className="text-center py-16">
            <Swords className="w-16 h-16 text-zinc-700 mx-auto mb-4" />
            <p className="text-zinc-500">No battles yet. Be the first to start one!</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {battles.map(b => (
              <BattleCard key={b.battle_id} battle={b} onSelect={setSelectedBattle} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function BattleCard({ battle, onSelect }) {
  const s1 = battle.slots[0];
  const s2 = battle.slots[1];
  const total = (s1?.votes || 0) + (s2?.votes || 0);
  const statusColor = { waiting: "bg-yellow-500/20 text-yellow-400", live: "bg-red-500/20 text-red-400", ended: "bg-zinc-700 text-zinc-400" };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="glass border border-white/10 rounded-xl p-5 hover:border-neon-cyan/30 transition cursor-pointer"
      onClick={() => onSelect(battle)}
      data-testid={`battle-card-${battle.battle_id}`}
    >
      <div className="flex justify-between items-start mb-4">
        <div>
          <h3 className="text-white font-bold text-lg">{battle.title}</h3>
          {battle.genre && <span className="text-zinc-500 text-xs">{battle.genre}</span>}
        </div>
        <Badge className={`${statusColor[battle.status] || statusColor.ended} border-0 text-xs uppercase`}>
          {battle.status === "live" && <span className="w-1.5 h-1.5 bg-red-400 rounded-full inline-block mr-1 animate-pulse" />}
          {battle.status}
        </Badge>
      </div>

      {/* VS Layout */}
      <div className="flex items-center gap-3">
        <div className="flex-1 text-center">
          <div className="w-12 h-12 rounded-full bg-neon-cyan/20 mx-auto mb-1 flex items-center justify-center text-neon-cyan font-bold">
            {s1?.user_name?.[0] || "?"}
          </div>
          <p className="text-white text-sm font-medium truncate">{s1?.user_name || "Waiting..."}</p>
          <p className="text-zinc-500 text-xs truncate">{s1?.beat_title || ""}</p>
          {total > 0 && <p className="text-neon-cyan font-bold mt-1">{s1?.votes || 0}</p>}
        </div>
        <div className="text-2xl font-heading text-zinc-600">VS</div>
        <div className="flex-1 text-center">
          <div className="w-12 h-12 rounded-full bg-neon-magenta/20 mx-auto mb-1 flex items-center justify-center text-neon-magenta font-bold">
            {s2?.user_name?.[0] || "?"}
          </div>
          <p className="text-white text-sm font-medium truncate">{s2?.user_name || "Waiting..."}</p>
          <p className="text-zinc-500 text-xs truncate">{s2?.beat_title || ""}</p>
          {total > 0 && <p className="text-neon-magenta font-bold mt-1">{s2?.votes || 0}</p>}
        </div>
      </div>

      {battle.status === "ended" && battle.winner_slot > 0 && (
        <div className="mt-3 text-center">
          <Badge className="bg-yellow-500/20 text-yellow-400 border-0">
            <Trophy className="w-3 h-3 mr-1" /> Winner: {battle.slots[battle.winner_slot - 1]?.user_name}
          </Badge>
        </div>
      )}

      <div className="flex justify-between mt-3 text-xs text-zinc-500">
        <span><Users className="w-3 h-3 inline mr-1" />{total} votes</span>
        <span><Clock className="w-3 h-3 inline mr-1" />{battle.duration_minutes}m</span>
      </div>
    </motion.div>
  );
}

function CreateBattleForm({ token, headers, onCreated }) {
  const [form, setForm] = useState({ title: "", description: "", genre: "", duration_minutes: 30, beat_id: "", beat_title: "", beat_image: "", beat_audio: "" });
  const [beats, setBeats] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    axios.get(`${API}/api/beats`).then(res => setBeats(res.data)).catch(() => {});
  }, []);

  const selectBeat = (beatId) => {
    const b = beats.find(x => x.beat_id === beatId);
    if (b) setForm(f => ({ ...f, beat_id: b.beat_id, beat_title: b.title, beat_image: b.image_url || "", beat_audio: b.audio_url || b.preview_url || "" }));
  };

  const submit = async () => {
    if (!token) { toast.error("Sign in first"); return; }
    if (!form.title.trim()) { toast.error("Enter a title"); return; }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/api/battles/create`, form, { headers });
      onCreated(res.data);
      toast.success("Battle created!");
    } catch { toast.error("Failed to create battle"); }
    finally { setSubmitting(false); }
  };

  return (
    <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }}
      className="glass border border-neon-cyan/30 rounded-xl p-6 mb-8 space-y-4"
    >
      <h3 className="font-heading text-lg uppercase">Start a Beat Battle</h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
          placeholder="Battle title..." className="bg-zinc-800 border-white/10" data-testid="battle-title-input" />
        <Input value={form.genre} onChange={e => setForm(f => ({ ...f, genre: e.target.value }))}
          placeholder="Genre (optional)" className="bg-zinc-800 border-white/10" />
      </div>
      <Textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
        placeholder="Description..." className="bg-zinc-800 border-white/10 min-h-[60px]" />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Select onValueChange={selectBeat}>
          <SelectTrigger className="bg-zinc-800 border-white/10" data-testid="battle-beat-select">
            <SelectValue placeholder="Select your beat" />
          </SelectTrigger>
          <SelectContent>
            {beats.map(b => <SelectItem key={b.beat_id} value={b.beat_id}>{b.title}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={String(form.duration_minutes)} onValueChange={v => setForm(f => ({ ...f, duration_minutes: parseInt(v) }))}>
          <SelectTrigger className="bg-zinc-800 border-white/10">
            <SelectValue placeholder="Duration" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="5">5 minutes</SelectItem>
            <SelectItem value="15">15 minutes</SelectItem>
            <SelectItem value="30">30 minutes</SelectItem>
            <SelectItem value="60">1 hour</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <Button onClick={submit} disabled={submitting} className="w-full bg-neon-cyan text-black font-bold" data-testid="submit-battle-btn">
        {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Swords className="w-4 h-4 mr-2" />}
        {submitting ? "Creating..." : "Start Battle"}
      </Button>
    </motion.div>
  );
}

function BattleArena({ battle: initialBattle, onBack, token, headers }) {
  const [battle, setBattle] = useState(initialBattle);
  const [playing, setPlaying] = useState(null); // 1 or 2
  const [voted, setVoted] = useState(false);
  const [timeLeft, setTimeLeft] = useState("");
  const [beats, setBeats] = useState([]);
  const audioRef = useRef(null);
  const wsRef = useRef(null);

  // Load available beats for joining
  useEffect(() => {
    if (battle.status === "waiting") {
      axios.get(`${API}/api/beats`).then(res => setBeats(res.data)).catch(() => {});
    }
  }, [battle.status]);

  // Fetch latest battle data
  useEffect(() => {
    axios.get(`${API}/api/battles/${battle.battle_id}`).then(res => setBattle(res.data)).catch(() => {});
  }, [battle.battle_id]);

  // WebSocket for live updates
  useEffect(() => {
    try {
      const ws = new WebSocket(`${WS_URL}/api/battles/ws/${battle.battle_id}`);
      ws.onmessage = (e) => {
        const data = JSON.parse(e.data);
        if (data.type === "vote_update") {
          setBattle(prev => ({
            ...prev,
            slots: prev.slots.map((s, i) => ({
              ...s,
              votes: i === 0 ? data.slot1_votes : data.slot2_votes
            })),
            total_votes: data.total_votes
          }));
        } else if (data.type === "battle_end") {
          setBattle(prev => ({ ...prev, status: "ended", winner_slot: data.winner_slot }));
        } else if (data.type === "battle_start") {
          axios.get(`${API}/api/battles/${battle.battle_id}`).then(res => setBattle(res.data)).catch(() => {});
        }
      };
      wsRef.current = ws;
    } catch {}
    return () => { if (wsRef.current) wsRef.current.close(); };
  }, [battle.battle_id]);

  // Countdown timer
  useEffect(() => {
    if (battle.status !== "live") return;
    const interval = setInterval(() => {
      const end = new Date(battle.end_time);
      const now = new Date();
      const diff = Math.max(0, end - now);
      const m = Math.floor(diff / 60000);
      const s = Math.floor((diff % 60000) / 1000);
      setTimeLeft(`${m}:${s.toString().padStart(2, "0")}`);
      if (diff <= 0) {
        setTimeLeft("0:00");
        setBattle(prev => ({ ...prev, status: "ended" }));
      }
    }, 1000);
    return () => clearInterval(interval);
  }, [battle.status, battle.end_time]);

  const playBeat = (slot) => {
    const audio = battle.slots[slot - 1]?.beat_audio;
    if (!audio || !audioRef.current) return;
    if (playing === slot) {
      audioRef.current.pause();
      setPlaying(null);
    } else {
      audioRef.current.src = audio;
      audioRef.current.play().catch(() => {});
      setPlaying(slot);
    }
  };

  const vote = async (slot) => {
    if (!token) { toast.error("Sign in to vote"); return; }
    if (voted) { toast("Already voted!"); return; }
    try {
      await axios.post(`${API}/api/battles/${battle.battle_id}/vote`, { slot }, { headers });
      setVoted(true);
      toast.success(`Voted for ${battle.slots[slot - 1]?.user_name}!`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to vote");
    }
  };

  const joinBattle = async (beatId) => {
    const b = beats.find(x => x.beat_id === beatId);
    if (!b) return;
    try {
      await axios.post(`${API}/api/battles/${battle.battle_id}/join`, {
        beat_id: b.beat_id, beat_title: b.title, beat_image: b.image_url || "", beat_audio: b.audio_url || b.preview_url || ""
      }, { headers });
      toast.success("Joined the battle!");
      const res = await axios.get(`${API}/api/battles/${battle.battle_id}`);
      setBattle(res.data);
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to join"); }
  };

  const s1 = battle.slots[0];
  const s2 = battle.slots[1];
  const total = (s1?.votes || 0) + (s2?.votes || 0);
  const s1Pct = total > 0 ? Math.round((s1.votes / total) * 100) : 50;
  const s2Pct = total > 0 ? 100 - s1Pct : 50;

  return (
    <div className="min-h-screen bg-dark pt-24 pb-16 px-4" data-testid="battle-arena">
      <audio ref={audioRef} onEnded={() => setPlaying(null)} />
      <div className="max-w-4xl mx-auto">
        <Button variant="ghost" onClick={onBack} className="text-zinc-400 mb-6" data-testid="battle-back-btn">
          <Swords className="w-4 h-4 mr-2" /> Back to Battles
        </Button>

        {/* Battle Header */}
        <div className="text-center mb-8">
          <Badge className={`${battle.status === "live" ? "bg-red-500/20 text-red-400" : battle.status === "waiting" ? "bg-yellow-500/20 text-yellow-400" : "bg-zinc-700 text-zinc-400"} border-0 mb-3`}>
            {battle.status === "live" && <span className="w-1.5 h-1.5 bg-red-400 rounded-full inline-block mr-1 animate-pulse" />}
            {battle.status.toUpperCase()}
          </Badge>
          <h1 className="font-heading text-2xl sm:text-3xl uppercase tracking-tight">{battle.title}</h1>
          {battle.status === "live" && (
            <div className="flex items-center justify-center gap-2 mt-2 text-neon-cyan">
              <Timer className="w-4 h-4" />
              <span className="font-mono text-lg">{timeLeft}</span>
            </div>
          )}
        </div>

        {/* VS Arena */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          {/* Slot 1 */}
          <SlotCard slot={s1} slotNum={1} playing={playing} onPlay={playBeat}
            onVote={vote} voted={voted} isLive={battle.status === "live"}
            isWinner={battle.status === "ended" && battle.winner_slot === 1} pct={s1Pct} />

          {/* VS Center */}
          <div className="flex flex-col items-center justify-center">
            <div className="relative">
              <div className="w-20 h-20 rounded-full border-2 border-white/20 flex items-center justify-center bg-zinc-900">
                <span className="font-heading text-2xl text-zinc-500">VS</span>
              </div>
              {battle.status === "live" && (
                <div className="absolute -inset-2 rounded-full border border-neon-cyan/30 animate-ping" />
              )}
            </div>
            <div className="mt-4 text-center">
              <p className="text-zinc-500 text-sm">{total} total votes</p>
            </div>
            {/* Vote bar */}
            {total > 0 && (
              <div className="w-full h-3 bg-zinc-800 rounded-full mt-3 overflow-hidden flex">
                <div className="bg-neon-cyan h-full transition-all duration-500" style={{ width: `${s1Pct}%` }} />
                <div className="bg-neon-magenta h-full transition-all duration-500" style={{ width: `${s2Pct}%` }} />
              </div>
            )}
          </div>

          {/* Slot 2 */}
          {s2.user_id ? (
            <SlotCard slot={s2} slotNum={2} playing={playing} onPlay={playBeat}
              onVote={vote} voted={voted} isLive={battle.status === "live"}
              isWinner={battle.status === "ended" && battle.winner_slot === 2} pct={s2Pct} />
          ) : (
            <div className="glass border border-dashed border-white/20 rounded-xl p-6 flex flex-col items-center justify-center min-h-[300px]" data-testid="join-battle-slot">
              <Swords className="w-12 h-12 text-zinc-600 mb-4" />
              <p className="text-zinc-400 text-sm mb-4">Waiting for challenger...</p>
              {token && beats.length > 0 && (
                <Select onValueChange={joinBattle}>
                  <SelectTrigger className="bg-zinc-800 border-white/10 w-full" data-testid="join-battle-select">
                    <SelectValue placeholder="Drop your beat to join" />
                  </SelectTrigger>
                  <SelectContent>
                    {beats.map(b => <SelectItem key={b.beat_id} value={b.beat_id}>{b.title}</SelectItem>)}
                  </SelectContent>
                </Select>
              )}
            </div>
          )}
        </div>

        {/* Winner Banner */}
        <AnimatePresence>
          {battle.status === "ended" && battle.winner_slot > 0 && (
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="glass border border-yellow-500/30 rounded-xl p-6 text-center"
            >
              <Trophy className="w-10 h-10 text-yellow-400 mx-auto mb-2" />
              <h2 className="font-heading text-xl uppercase text-yellow-400">
                {battle.slots[battle.winner_slot - 1]?.user_name} Wins!
              </h2>
              <p className="text-zinc-400 text-sm mt-1">
                {battle.slots[battle.winner_slot - 1]?.votes} votes vs {battle.slots[battle.winner_slot === 1 ? 1 : 0]?.votes} votes
              </p>
            </motion.div>
          )}
          {battle.status === "ended" && battle.winner_slot === 0 && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}
              className="glass border border-white/10 rounded-xl p-6 text-center"
            >
              <p className="text-zinc-400">It's a tie!</p>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}

function SlotCard({ slot, slotNum, playing, onPlay, onVote, voted, isLive, isWinner, pct }) {
  const color = slotNum === 1 ? "neon-cyan" : "neon-magenta";
  const bgColor = slotNum === 1 ? "bg-neon-cyan" : "bg-neon-magenta";

  return (
    <motion.div
      initial={{ opacity: 0, x: slotNum === 1 ? -30 : 30 }}
      animate={{ opacity: 1, x: 0 }}
      className={`glass border rounded-xl p-5 relative overflow-hidden ${isWinner ? "border-yellow-500/50" : "border-white/10"}`}
      data-testid={`battle-slot-${slotNum}`}
    >
      {isWinner && (
        <div className="absolute top-2 right-2">
          <Trophy className="w-6 h-6 text-yellow-400" />
        </div>
      )}

      {/* Cover */}
      <div className="aspect-square rounded-lg overflow-hidden mb-4 bg-zinc-800 relative">
        {slot.beat_image ? (
          <img src={slot.beat_image} alt={slot.beat_title} className="w-full h-full object-cover" loading="lazy" />
        ) : (
          <div className={`w-full h-full flex items-center justify-center bg-${color}/10`}>
            <Zap className={`w-12 h-12 text-${color}`} />
          </div>
        )}
        {/* Play button */}
        {slot.beat_audio && (
          <button
            onClick={() => onPlay(slotNum)}
            className={`absolute bottom-3 right-3 w-10 h-10 rounded-full flex items-center justify-center transition ${
              playing === slotNum ? `${bgColor} text-black` : "bg-black/60 text-white hover:bg-black/80"
            }`}
            data-testid={`play-slot-${slotNum}`}
          >
            {playing === slotNum ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4 ml-0.5" />}
          </button>
        )}
      </div>

      <p className="text-white font-bold truncate">{slot.user_name}</p>
      <p className="text-zinc-400 text-sm truncate mb-3">{slot.beat_title}</p>

      <div className="flex items-center justify-between">
        <span className={`text-2xl font-heading text-${color}`}>{slot.votes || 0}</span>
        <span className="text-zinc-500 text-xs">{pct}%</span>
      </div>

      {isLive && !voted && (
        <Button
          onClick={() => onVote(slotNum)}
          className={`w-full mt-3 ${bgColor} text-black font-bold`}
          data-testid={`vote-slot-${slotNum}`}
        >
          <Zap className="w-4 h-4 mr-1" /> Vote
        </Button>
      )}
    </motion.div>
  );
}
